import { Injectable, inject } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';

import { firstValueFrom } from 'rxjs';

import { PartnerPlayedFetchResult } from 'src/app/models/partner-played.model';

interface PartnerEndpointSummary {
  partnerId: string;
  enabled: boolean;
}

/**
 * Owner-only access to the partner-hosted endpoints (played rooms API). The list of enabled partners is fetched
 * once and cached so that a page full of applicant cards asks the backend a single time.
 */
@Injectable({ providedIn: 'root' })
export class PartnerEndpointsService {
  private functions = inject(AngularFireFunctions);

  private enabledPartnerIds: Promise<Set<string>> | null = null;

  /** Resolves to the partner ids that have an enabled endpoint. Only call this as an owner; others get an error. */
  async loadEnabledPartnerIds(): Promise<Set<string>> {
    this.enabledPartnerIds ??= this.listEnabledPartnerIds().catch((error: unknown) => {
      this.enabledPartnerIds = null;  // let a later call retry
      throw error;
    });
    return this.enabledPartnerIds;
  }

  private async listEnabledPartnerIds(): Promise<Set<string>> {
    const endpoints = await firstValueFrom(
        this.functions.httpsCallable<Record<string, never>, PartnerEndpointSummary[]>('listPartnerEndpoints')({}));
    return new Set(endpoints.filter(endpoint => endpoint.enabled).map(endpoint => endpoint.partnerId));
  }

  /** Calls the partner's played-rooms endpoint for this TERPECA user, via the owner-only proxy function. */
  async fetchPlayedRooms(partnerId: string, uid: string): Promise<PartnerPlayedFetchResult> {
    return firstValueFrom(
        this.functions.httpsCallable<{ partnerId: string; uid: string }, PartnerPlayedFetchResult>(
            'fetchPartnerPlayedRooms')({ partnerId, uid }));
  }
}
